import { useState } from 'react';
import type { Bookmark } from '@stashmark/shared';
import { BookmarkList } from '@/components/BookmarkList/BookmarkList';
import { AddBookmarkForm } from '@/components/AddBookmarkForm/AddBookmarkForm';
import { EditBookmarkDialog } from '@/components/EditBookmarkDialog/EditBookmarkDialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sidebar } from '@/components/Sidebar/Sidebar';
import { TopBar } from '@/components/TopBar/TopBar';
import { MobileNav } from '@/components/MobileNav/MobileNav';
import { Filter, ArrowUpDown } from 'lucide-react';
import { useResponsive } from '@/hooks/useBreakpoint';
import { cn } from '@/lib/utils';

export const Dashboard = () => {
  const { isMobile } = useResponsive();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editBookmark, setEditBookmark] = useState<Bookmark | null>(null);
  const [deleteBookmark, setDeleteBookmark] = useState<Bookmark | null>(null);

  const handleEdit = (bookmark: Bookmark) => {
    setEditBookmark(bookmark);
  };

  const handleDelete = (bookmark: Bookmark) => {
    setDeleteBookmark(bookmark);
  };

  const handleCloseEditDialog = () => {
    setEditBookmark(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteBookmark(null);
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {!isMobile && <Sidebar />}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar />
        <main
          className={cn(
            'flex-1 overflow-y-auto bg-background px-4 py-6 md:px-8 md:py-10',
            isMobile && 'pb-24' // Extra padding for bottom nav on mobile
          )}
        >
          <div className="mb-6 flex flex-col gap-4 md:mb-10 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Design Inspiration
              </h1>
              <p className="mt-1 text-sm text-muted-foreground md:mt-2 md:text-base">
                Curated collection of UI/UX trends and web components.
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              {showAddForm ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                  <Card className="w-full max-w-lg border-2 border-primary/20 shadow-xl">
                    <CardHeader>
                      <CardTitle>Add New Bookmark</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <AddBookmarkForm
                        onSuccess={handleAddSuccess}
                        onCancel={() => setShowAddForm(false)}
                      />
                    </CardContent>
                  </Card>
                </div>
              ) : null}

              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'default'}
                className="gap-2 rounded-xl text-muted-foreground hover:text-foreground"
              >
                <Filter className="h-4 w-4" />
                Filter
              </Button>
              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'default'}
                className="gap-2 rounded-xl text-muted-foreground hover:text-foreground"
              >
                <ArrowUpDown className="h-4 w-4" />
                Sort
              </Button>
            </div>
          </div>

          <BookmarkList onEdit={handleEdit} onDelete={handleDelete} />
        </main>
      </div>

      {isMobile && <MobileNav />}

      <EditBookmarkDialog
        bookmark={editBookmark}
        isOpen={editBookmark !== null}
        onClose={handleCloseEditDialog}
      />

      <DeleteConfirmDialog
        bookmark={deleteBookmark}
        isOpen={deleteBookmark !== null}
        onClose={handleCloseDeleteDialog}
      />
    </div>
  );
};
